import type { AgencyTheme } from '../../types';
import { Home, Package } from 'lucide-react';

interface ThemePreviewProps {
  theme: AgencyTheme;
}

const PREVIEW_WIDTH = 280;

/** Miniature réaliste de la page d'accueil agence */
function HomepageMiniPreview({ theme }: { theme: AgencyTheme }) {
  const p = theme.primaryColor;
  const s = theme.secondaryColor;
  const headerText = theme.accentColor || p;
  const t = theme.textColor;
  const bg = theme.backgroundColor;

  return (
    <div
      className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-md bg-white shrink-0"
      style={{ width: PREVIEW_WIDTH, fontFamily: theme.font }}
    >
      {/* Header (texte du header = accentColor) */}
      <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: s }}>
        <div className="w-7 h-7 rounded" style={{ backgroundColor: `${headerText}50` }} />
        <div className="flex gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: headerText }} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: headerText }} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: headerText }} />
        </div>
        <div className="w-12 h-5 rounded-full text-[10px] flex items-center justify-center font-medium" style={{ backgroundColor: p, color: s }}>
          Devis
        </div>
      </div>
      {/* Hero */}
      <div className="relative h-20 bg-gradient-to-br from-gray-400 to-gray-600">
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 flex flex-col justify-center px-3">
          <div className="h-3 w-3/4 rounded bg-white/90 mb-1.5" />
          <div className="h-2 w-full rounded bg-white/60 mb-2.5" />
          <div className="flex gap-2">
            <div className="h-4 w-14 rounded-full" style={{ backgroundColor: p }} />
            <div className="h-4 w-12 rounded-full bg-white/90" />
          </div>
        </div>
      </div>
      {/* Nos offres */}
      <div className="px-3 py-2 bg-gray-50">
        <div className="h-2 w-14 rounded mb-2" style={{ backgroundColor: t, opacity: 0.3 }} />
        <div className="flex gap-2">
          <div className="flex-1 h-16 rounded border" style={{ borderColor: `${p}40`, backgroundColor: `${p}08` }} />
          <div className="flex-1 h-16 rounded border" style={{ borderColor: `${p}40`, backgroundColor: `${p}08` }} />
        </div>
      </div>
      {/* Prestations */}
      <div className="px-3 py-2" style={{ backgroundColor: bg }}>
        <div className="h-2 w-16 rounded mb-2" style={{ backgroundColor: t, opacity: 0.25 }} />
        <div className="flex gap-1.5">
          <div className="flex-1 h-6 rounded-l border-l-2" style={{ borderLeftColor: p, backgroundColor: 'white' }} />
          <div className="flex-1 h-6 rounded-l border-l-2" style={{ borderLeftColor: p, backgroundColor: 'white' }} />
        </div>
      </div>
      {/* Contact */}
      <div className="px-3 py-2 bg-white border-t border-gray-100">
        <div className="h-2 w-20 rounded mb-1.5" style={{ backgroundColor: t, opacity: 0.2 }} />
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: `${p}25` }} />
          <div className="h-3 flex-1 rounded bg-gray-100" />
        </div>
      </div>
    </div>
  );
}

/** Miniature réaliste de la page package */
function PackagePageMiniPreview({ theme }: { theme: AgencyTheme }) {
  const p = theme.primaryColor;
  const s = theme.secondaryColor;
  const t = theme.textColor;
  const bg = theme.backgroundColor;

  return (
    <div
      className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-md bg-white shrink-0"
      style={{ fontFamily: theme.font, width: PREVIEW_WIDTH }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ backgroundColor: s }}>
        <div className="w-7 h-7 rounded bg-white/30" />
        <div className="w-12 h-5 rounded text-[10px] flex items-center justify-center font-medium" style={{ backgroundColor: p, color: s }}>
          Retour
        </div>
      </div>
      {/* Hero image + titre */}
      <div className="relative h-16 bg-gradient-to-br from-gray-500 to-gray-700">
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute bottom-2 left-3 right-3">
          <div className="h-3 w-full rounded bg-white/95 mb-1.5" />
          <div className="flex gap-1.5">
            <div className="h-2.5 w-10 rounded bg-white/70" />
            <div className="h-2.5 w-8 rounded bg-white/50" />
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 px-2">
        {['Résumé', 'Itinéraire', 'Galerie'].map((label, i) => (
          <div
            key={label}
            className="px-2.5 py-2 text-[10px] font-medium border-b-2 -mb-px"
            style={{
              color: i === 0 ? p : t,
              borderBottomColor: i === 0 ? p : 'transparent',
              opacity: i === 0 ? 1 : 0.7,
            }}
          >
            {label}
          </div>
        ))}
      </div>
      {/* Contenu */}
      <div className="px-3 py-2.5" style={{ backgroundColor: bg }}>
        <div className="space-y-2">
          <div className="h-2.5 w-4/5 rounded" style={{ backgroundColor: t, opacity: 0.15 }} />
          <div className="h-2.5 w-full rounded" style={{ backgroundColor: t, opacity: 0.08 }} />
          <div className="h-2.5 w-5/6 rounded" style={{ backgroundColor: t, opacity: 0.08 }} />
        </div>
        <div className="mt-2.5 p-2 rounded border" style={{ borderColor: `${p}30`, backgroundColor: 'white' }}>
          <div className="h-2.5 w-14 rounded mb-1.5" style={{ backgroundColor: p, opacity: 0.3 }} />
          <div className="h-4 w-16 rounded text-[10px] flex items-center justify-center font-medium text-white" style={{ backgroundColor: p }}>
            Réserver
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ThemePreview({ theme }: ThemePreviewProps) {
  return (
    <div className="space-y-5 p-4 rounded-xl bg-gray-50 border border-gray-200">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Home className="w-4 h-4" />
        <span>Page d&apos;accueil</span>
      </div>
      <HomepageMiniPreview theme={theme} />

      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 pt-4 border-t border-gray-200">
        <Package className="w-4 h-4" />
        <span>Page voyage</span>
      </div>
      <PackagePageMiniPreview theme={theme} />
    </div>
  );
}
